import React from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import uuid from 'uuid/v4'
import Div from '@jarvis.oddle/core/components/Div'
import Flex from '@jarvis.oddle/core/components/Flex'
import Backdrop from '@jarvis.oddle/core/components/Modal/ModalBackdrop'
import Dropdown from '@jarvis.oddle/core/components/Dropdown'
import Transition from '@jarvis.oddle/core/components/Transition'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import IconArrow, {
  arrowDirections
} from '@jarvis.oddle/core/components/Icons/IconArrow'
import { P, Text } from '@jarvis.oddle/core/components/Typo'
import {
  dropShadows,
  dropShadowVariations,
  marginSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import * as bodyScrolling from '@jarvis.oddle/core/tools/bodyScrolling'
import VerticalNavigation from 'src/components/molecules/VerticalNavigation'

const navigationSize = '40px'

const labelStyle = css`
  height: ${navigationSize};
`

const Container = styled(Flex).attrs(() => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center'
}))`
  position: relative;
  z-index: 1;
  width: 100%;
  padding: ${padding[paddingSizes.small]} ${padding[paddingSizes.small]};
  color: ${(props) =>
    props.expandDownwards
      ? props.theme.colorBodyFont
      : props.theme.buttonTextColor};
  background-color: ${(props) =>
    props.expandDownwards
      ? props.theme.colorContainerColor
      : props.theme.buttonColor};
  box-shadow: ${dropShadows[dropShadowVariations.light]};
  ${labelStyle}
`

const DropdownLabel = styled(Div)`
  ${labelStyle}
`

const DropdownContainer = styled(Div)`
  position: absolute;
  width: 100%;
  ${(props) =>
    props.expandDirection === 'down'
      ? `
    top: 100%;
  `
      : ''}

  ${(props) =>
    props.expandDirection === 'up'
      ? `
    bottom: 100%;
  `
      : ''}
`

const StyledVerticalNavigation = styled(VerticalNavigation)`
  position: relative;
  width: 100%;
  padding: ${padding[paddingSizes.extraSmall]} 0;
  border-radius: 0;
`

const StyledDropdownText = styled(Text)`
  text-decoration: underline;
`

const DropdownLabelContent = ({
  labelText,
  dropdownText,
  onClick,
  isDropdownOpening,
  selected,
  expandDownwards
}) => (
  <DropdownLabel>
    <Container
      onClick={onClick}
      selected={selected}
      expandDownwards={expandDownwards}
      id='product-section-mobile'
    >
      <P
        bold
        margin={marginSizes.none}
        marginRight={marginSizes.small}
        color='inherit'
      >
        {labelText}
      </P>
      <Div>
        <StyledDropdownText
          margin={marginSizes.none}
          marginRight={marginSizes.small}
          color='inherit'
        >
          {dropdownText}
        </StyledDropdownText>
        <IconArrow
          iconSize='small'
          direction={
            isDropdownOpening
              ? arrowDirections.up
              : expandDownwards
              ? arrowDirections.down
              : arrowDirections.right
          }
          fill='currentColor'
        />
      </Div>
    </Container>
  </DropdownLabel>
)
DropdownLabelContent.propTypes = {
  labelText: PropTypes.string,
  dropdownText: PropTypes.string,
  onClick: PropTypes.func,
  isDropdownOpening: PropTypes.bool,
  selected: PropTypes.bool,
  expandDownwards: PropTypes.bool
}

const DropdownContent = React.memo(
  ({
    isDropdownOpening,
    items,
    selectedItemId,
    closeDropdown,
    onClickItem
  }: {
    isDropdownOpening: boolean
    items: Record<string, string>[]
    selectedItemId: string
    closeDropdown: () => void
    onClickItem: (...params) => void
  }): JSX.Element => {
    const id = React.useMemo(() => uuid(), [])
    const handleItemClick = React.useCallback(
      (...args) => {
        bodyScrolling.release(id)
        onClickItem(...args)
      },
      [id, onClickItem]
    )

    const releaseScrollLock = React.useCallback(
      () => bodyScrolling.release(id),
      [id]
    )

    React.useEffect(() => {
      if (isDropdownOpening) {
        bodyScrolling.lock(id)
      }
      return () => bodyScrolling.release(id)
    }, [id, isDropdownOpening, releaseScrollLock])

    return (
      <Transition show={isDropdownOpening} mountOnEnter unmountOnExit>
        <Div onClick={closeDropdown}>
          <StyledVerticalNavigation
            items={items}
            selectedItemId={selectedItemId}
            onClickItem={handleItemClick}
          />
          <Backdrop show transitionDuration={100} />
        </Div>
      </Transition>
    )
  }
)

export const MobileNavigation = React.memo(
  ({
    className,
    items,
    selectedItemId = '',
    expandDownwards = false,
    onClickItem
  }: {
    className?: string
    items: Record<string, string>[]
    selectedItemId: string
    onClickItem: (event, id) => void
    expandDownwards: boolean
  }) => {
    const { t } = React.useContext(TranslationContext)
    const matchingCategories = items?.filter((c) => c.id === selectedItemId)
    const selectedCategory =
      matchingCategories?.length > 0 ? matchingCategories[0] : items?.[0]
    const labelText = selectedCategory && selectedCategory.name

    return (
      <Div className={className}>
        <Dropdown
          DropdownContainer={({ children }) => (
            <DropdownContainer
              expandDirection={expandDownwards ? 'down' : 'up'}
            >
              {children}
            </DropdownContainer>
          )}
          renderLabel={({ isDropdownOpening, toggleDropdown }) => (
            <DropdownLabelContent
              isDropdownOpening={isDropdownOpening}
              onClick={toggleDropdown}
              labelText={labelText}
              dropdownText={t('More')}
              selected={labelText !== ''}
              expandDownwards={expandDownwards}
            />
          )}
          renderDropdown={({ isDropdownOpening, closeDropdown }) => (
            <DropdownContent
              isDropdownOpening={isDropdownOpening}
              closeDropdown={closeDropdown}
              items={items}
              onClickItem={onClickItem}
              selectedItemId={selectedItemId}
            />
          )}
        />
      </Div>
    )
  }
)

export default MobileNavigation
